import { useQuery } from "@tanstack/react-query";
import { Link as LinkIcon, MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Button } from "~/components/ui/button";
import { FormMessage } from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { eventApi } from "~/lib/api/event";
import { cn } from "~/lib/utils";
import { MapPreview } from "./map-preview";
import { type EventFormData, isGoogleMapsUrl } from "./utils";

type LocationInputProps = {
	disabled?: boolean;
};

export function LocationInput({ disabled }: LocationInputProps) {
	const form = useFormContext<EventFormData>();
	const watchedLocation = form.watch("locationLink");
	const coordinates = form.watch("locationCoordinates") as string;
	const [isMapLink, setIsMapLink] = useState(false);

	const { isLoading: isResolvingUrl } = useQuery({
		queryKey: ["resolveGoogleMapsShortLink", watchedLocation],
		queryFn: async () => {
			if (watchedLocation && watchedLocation.trim() !== "") {
				const { data } =
					await eventApi.resolveGoogleMapsShortLink(watchedLocation);
				form.setValue("locationCoordinates", data.formattedCoordinates);
				return data;
			}
		},
		enabled: Boolean(
			watchedLocation &&
				watchedLocation.trim().length > 0 &&
				isGoogleMapsUrl(watchedLocation),
		),
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		retry: 3,
		retryDelay: 1000,
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnMount: "always",
	});

	// Check if the current value is a Google Maps URL
	useEffect(() => {
		if (watchedLocation) {
			const isMapUrl = isGoogleMapsUrl(watchedLocation);

			if (isMapUrl) {
				setIsMapLink(true);
				form.setValue("locationUrl", watchedLocation);
			} else {
				setIsMapLink(false);
				form.setValue("locationCoordinates", undefined);
				form.setValue("locationUrl", undefined);
			}
		} else {
			setIsMapLink(false);
			form.setValue("locationCoordinates", undefined);
			form.setValue("locationUrl", undefined);
		}
	}, [watchedLocation, form]);

	const handleOpenMapPicker = () => {
		// For now, just open Google Maps in a new tab
		// Later we could implement a proper map picker UI
		window.open("https://www.google.com/maps", "_blank");
	};

	return (
		<div className="flex flex-col space-y-2">
			<div className="relative flex items-center">
				<div className="absolute left-3 flex-shrink-0 z-10">
					{isMapLink ? (
						<LinkIcon className="h-5 w-5 text-accent" />
					) : (
						<MapPin className="h-5 w-5 text-white/50" />
					)}
				</div>
				<Input
					id="locationLink"
					placeholder={
						isMapLink ? "Paste Google Maps link" : "Enter the venue address"
					}
					className={cn(
						"bg-primary/10 border-white/10 text-white placeholder:text-white/50 focus:border-accent/50 focus:ring-accent/20 pl-10 pr-16",
						"text-ellipsis overflow-hidden",
						isMapLink && "text-accent",
						disabled && "opacity-50",
					)}
					disabled={disabled}
					{...form.register("locationLink")}
				/>
				<div className="absolute right-0 flex">
					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="h-6 px-1 mr-2 text-white/70 hover:text-white z-10"
						onClick={handleOpenMapPicker}
						disabled={disabled}
					>
						Map
					</Button>
				</div>
			</div>

			{isMapLink && (
				<div className="text-xs text-accent flex items-center gap-1.5">
					<LinkIcon className="h-3 w-3 flex-shrink-0" />
					{isResolvingUrl ? (
						<span className="flex items-center gap-1.5">
							<span className="h-2 w-2 rounded-full bg-accent/80 animate-pulse" />
							Resolving Google Maps link...
						</span>
					) : coordinates ? (
						<span>Google Maps link resolved</span>
					) : (
						<span>Google Maps link detected but couldn't extract location</span>
					)}
				</div>
			)}

			{form.formState.errors.locationLink && (
				<FormMessage className="text-red-500 text-sm">
					{form.formState.errors.locationLink.message}
				</FormMessage>
			)}

			{/* Map preview */}
			{coordinates && coordinates.trim() !== "" && (
				<div className="mt-3 p-2 sm:p-3 bg-primary/20 border border-white/10 rounded-lg">
					<MapPreview
						coordinates={coordinates}
						className="rounded-md overflow-hidden shadow-sm"
					/>
					<div className="mt-2 sm:mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
						<div className="text-xs text-white/60 truncate">
							Coordinates: {coordinates}
						</div>
						<a
							href={`https://www.google.com/maps/search/?api=1&query=${coordinates}`}
							target="_blank"
							rel="noopener noreferrer"
							className="text-xs text-accent flex items-center gap-1 hover:underline"
						>
							<LinkIcon className="h-3 w-3" />
							View in Google Maps
						</a>
					</div>
				</div>
			)}
		</div>
	);
}
